import { toast } from 'sonner';

/**
 * Keeps an open tab from drifting behind a deploy.
 *
 * Two problems, one module. The loud one: every route is `lazy()`, so a tab
 * that was open across a deploy asks for a chunk hash that no longer exists on
 * disk and the route renders nothing at all. The quiet one: a tab left open
 * for days keeps running old code against a moving API.
 *
 * `version.json` is written by the build (see `vite.config.ts`) and carries the
 * commit the bundle came from. Polling it is cheap — a few dozen bytes, and
 * only while the tab is actually being looked at.
 */

/** Where the build writes its id. Served next to index.html. */
const VERSION_URL = '/version.json';

/** How often to look while the tab is in the foreground. */
const POLL_MS = 5 * 60 * 1000;

/**
 * Marks that we have already reloaded for a given build.
 *
 * The one genuinely dangerous failure here is a reload loop: if the reload
 * lands back on the same stale bundle — a proxy still holding the old
 * index.html, an offline cache — an unguarded handler will spin the tab
 * forever. Session storage is the right scope: it survives the reload we are
 * about to trigger and dies with the tab.
 */
const RELOAD_KEY = 'partapp:reloaded-for-build';

let started = false;
let notified = false;

/** The build this tab is running, baked in at compile time. */
export const currentBuildId = __BUILD_ID__;

type VersionFile = { buildId?: string };

/** The build the server is serving right now, or null if we could not tell. */
async function fetchServerBuildId(): Promise<string | null> {
  try {
    // `no-store` matters as much as the nginx header: without it the browser
    // will happily answer this from its own cache and we would compare the
    // running build against itself forever.
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionFile;
    return typeof data.buildId === 'string' ? data.buildId : null;
  } catch {
    // Offline, or mid-deploy while the file is being replaced. Not worth
    // telling anyone about — the next poll will settle it.
    return null;
  }
}

/**
 * Reloads the page, guarding against doing it twice for the same build.
 *
 * @param serverBuildId the build we are reloading in order to pick up
 */
function reloadOnce(serverBuildId: string | null) {
  const target = serverBuildId ?? 'unknown';
  if (sessionStorage.getItem(RELOAD_KEY) === target) return;
  try {
    sessionStorage.setItem(RELOAD_KEY, target);
  } catch {
    // Private mode with storage blocked. Reloading once without the guard is
    // still better than leaving a broken screen, and the preload handler that
    // calls this only fires on an actual failed import.
  }
  window.location.reload();
}

/**
 * Offers the new version rather than taking the page away.
 *
 * An admin mid-form would lose what they typed if we reloaded underneath them,
 * and this console is mostly forms. The one case where we do reload without
 * asking is a chunk that failed to load, because there is nothing on screen to
 * protect at that point.
 */
function offerReload() {
  if (notified) return;
  notified = true;

  toast('A new version of PartApp is available', {
    description: 'Reload to get the latest. Anything unsaved will be lost.',
    duration: Infinity,
    action: {
      label: 'Reload',
      onClick: () => window.location.reload(),
    },
    onDismiss: () => {
      // Let it come back on the next poll rather than suppressing it for the
      // life of the tab — a dismissed prompt should not mean never again.
      notified = false;
    },
  });
}

async function checkNow() {
  const serverBuildId = await fetchServerBuildId();
  if (!serverBuildId || serverBuildId === currentBuildId) return;
  offerReload();
}

/**
 * Starts watching for new deploys. Call once, from the app root.
 *
 * Safe to call more than once; only the first call does anything.
 */
export function startVersionWatch() {
  if (started || import.meta.env.DEV) return;
  started = true;

  // A chunk that 404s means the deploy already happened and this tab cannot
  // render the route it was asked for. Nothing to preserve, so reload straight
  // away instead of asking.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    void fetchServerBuildId().then(reloadOnce);
  });

  // Coming back to a tab is the moment someone is most likely to have been
  // away across a deploy, and the cheapest time to interrupt them.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkNow();
  });

  const timer = window.setInterval(() => {
    // Polling a background tab wakes a sleeping laptop for nothing.
    if (document.visibilityState === 'visible') void checkNow();
  }, POLL_MS);

  window.addEventListener('pagehide', () => window.clearInterval(timer));

  void checkNow();
}
